/* Bulk on-load prerender guard, 2026-08-28.

   Finding: theme.js's onIntersection_fn preloads every currently-unchecked
   option the moment a <variant-picker> scrolls into view - intended as a
   one-time, page-load courtesy. It is not one-time. selectCombination()
   replaces the entire <variant-picker> subtree with a fresh instance on
   every option change (confirmed: querying the element before and after a
   click returns a different node), and that fresh instance's own
   constructor creates a new IntersectionObserver and calls .observe(this)
   again - since the element is already on screen, the callback fires
   immediately, re-running the bulk pass for the new post-selection
   context. Confirmed by direct measurement: selecting Custom Logo on Metal
   Wallet fired 8 requests (3 unchecked Color values + 4 unchecked
   Engraving values + 1 unchecked Design value - exactly the option count),
   on top of the one real fetch for the combination actually selected.
   With IntersectionObserver.observe() disabled for <variant-picker>
   targets entirely, that dropped to exactly 1 request. This is the
   dominant, repeating source of the preload waste this session was
   chasing - bigger than the hover-sweep case handled separately in
   assets/ascot-preload-throttle.js - and it fires on every option change
   on every product with more than one variant, not just Custom Logo.

   Fix: let the first observe() call for a given section-id proceed
   normally (preserves the intended "preload options as they scroll into
   view" benefit on initial page load), and skip every later observe()
   call for a re-rendered instance of the same section. The only
   combinations a later bulk pass would newly prefetch are ones whose
   cache key changed because some OTHER option changed - exactly the
   combinations the customer is least likely to want next. A real hover
   or click after this point still preloads normally through
   onOptionPreload_fn (via the debounce in ascot-preload-throttle.js),
   untouched by this.

   theme.js's VariantPicker only ever calls .observe(this) and
   .unobserve(this) on its IntersectionObserver instance (confirmed by
   reading its connectedCallback/disconnectedCallback) - nothing else, so
   this minimal wrapper is sufficient. Every other observe() call on the
   page (reveal-on-scroll, lazy-loaded images) passes straight through
   unaffected, since the check only applies to <variant-picker> targets.

   Why this file loads early, unlike every other Ascot override: the
   initial page-load <variant-picker> is upgraded, and its
   IntersectionObserver constructed, synchronously inside theme.js's own
   customElements.define() call - before any deferred script has run.
   Confirmed the hard way: with this logic living in the deferred
   ascot-preload-throttle.js (loaded after theme.js, same as every other
   override on this theme), the request count did not drop, because the
   first, page-load .observe() call happened against the real, unwrapped
   IntersectionObserver before this script had installed the wrapper - so
   the guard's own bookkeeping saw the SECOND (post-click) instance's
   observe() call as if it were the first, and let it through. This file
   is loaded via a plain, non-deferred <script> tag placed before the
   importmap/theme.js script tags in theme.liquid specifically so the
   wrapper is installed before theme.js executes at all. It still never
   edits theme.js itself - override pattern preserved, only the load
   timing is different from the rest of this theme's overrides.
*/
(function () {
  var seenSectionIds = new Set();
  var OriginalIntersectionObserver = window.IntersectionObserver;

  function AscotIntersectionObserverWrapper(callback, options) {
    var real = new OriginalIntersectionObserver(callback, options);
    this.observe = function (target) {
      if (target && target.tagName === 'VARIANT-PICKER') {
        var sectionId = target.getAttribute('section-id');
        if (sectionId) {
          if (seenSectionIds.has(sectionId)) return;
          seenSectionIds.add(sectionId);
        }
      }
      return real.observe(target);
    };
    this.unobserve = function (target) { return real.unobserve(target); };
    this.disconnect = function () { return real.disconnect(); };
    this.takeRecords = function () { return real.takeRecords(); };
  }

  window.IntersectionObserver = AscotIntersectionObserverWrapper;
})();

/* Pass C migration, 2026-08-28: ProductCard secondary media cleanup.
   Stock onSwatchChanged_fn (js/common/product/product-card.js) only calls
   secondaryMediaElement.replaceWith(newSecondaryMediaElement) when both a
   current and a new secondary media element exist. Switching to a variant
   with no secondary/hover image left the previous variant's secondary
   image in place, mismatched with the now-selected variant. This fix was
   already living directly in theme.js (an added else-if branch removing
   the stale element) with no record of it beyond git history - reverted
   theme.js back to stock and moved the fix here.

   Not a tag rename, unlike the other Pass C migrations: theme.js's own
   ProductList.reveal() (js/common/product/product-list.js) selects
   reveal-on-scroll elements by the literal tag name product-card, and so
   does the Stage 2 will-change fix in assets/ascot-reveal-performance.js.
   Renaming the tag would silently break both. Instead, this class is
   registered under the unchanged tag name product-card, and wins the
   race against theme.js's own customElements.define("product-card", ...)
   call by loading first - stock code checks
   !window.customElements.get(tag) before defining and no-ops when it
   finds the tag already taken, so its own, unfixed ProductCard class
   never registers at all. This is exactly why this fix lives in this
   file specifically, rather than in the normal deferred ascot-unify.js:
   it has to install before theme.js executes, the same requirement the
   IntersectionObserver guard above this comment has, for the same reason
   (see that section's own header for the full timing explanation).

   Event delegation here uses plain addEventListener + closest() rather
   than theme.js's internal Delegate class, since that class isn't
   exported for an external script to reuse - functionally equivalent to
   the stock Delegate-based wiring (same events, same capture flag on
   pointerover, same selectors).
*/
(function () {
  function generateSrcset(imageObjectOrString, widths) {
    var imageUrl, maxWidth;
    if (typeof imageObjectOrString === 'string') {
      imageUrl = new URL(imageObjectOrString.indexOf('//') === 0 ? 'https:' + imageObjectOrString : imageObjectOrString);
      maxWidth = parseInt(imageUrl.searchParams.get('width'));
    } else {
      imageUrl = new URL(imageObjectOrString.src.indexOf('//') === 0 ? 'https:' + imageObjectOrString.src : imageObjectOrString.src);
      maxWidth = imageObjectOrString.width;
    }
    return widths.filter(function (width) { return width <= maxWidth; }).map(function (width) {
      imageUrl.searchParams.set('width', width.toString());
      return imageUrl.href + ' ' + width + 'w';
    }).join(', ');
  }

  function createMediaImg(media, widths, attributes) {
    var image = new Image(media.preview_image.width, media.preview_image.height);
    var src = media.preview_image.src;
    var featuredMediaUrl = new URL(src.indexOf('//') === 0 ? 'https:' + src : src);
    for (var attributeKey in attributes) {
      image.setAttribute(attributeKey, attributes[attributeKey]);
    }
    image.alt = media.alt || '';
    image.src = featuredMediaUrl.href;
    image.srcset = generateSrcset(media.preview_image, widths);
    return image;
  }

  var MEDIA_WIDTHS = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800];

  class AscotProductCard extends HTMLElement {
    connectedCallback() {
      this._onChange = this._onChange.bind(this);
      this._onPointerOver = this._onPointerOver.bind(this);
      this.addEventListener('change', this._onChange);
      this.addEventListener('pointerover', this._onPointerOver, true);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('pointerover', this._onPointerOver, true);
    }

    _createMediaImg(media, className, sizes) {
      return createMediaImg(media, MEDIA_WIDTHS, { class: className, sizes: sizes });
    }

    _onPointerOver(event) {
      var target = event.target.closest('.product-card__info [type="radio"] + label');
      if (!target) return;
      var control = target.control;
      var primaryMediaElement = this.querySelector('.product-card__image--primary');
      if (control.hasAttribute('data-variant-media')) {
        this._createMediaImg(JSON.parse(control.getAttribute('data-variant-media')), primaryMediaElement.className, primaryMediaElement.sizes);
      }
    }

    async _onChange(event) {
      var target = event.target.closest('.product-card__info [type="radio"]');
      if (!target) return;
      if (target.hasAttribute('data-product-url')) {
        this.querySelectorAll('a[href^="' + Shopify.routes.root + 'products/"]').forEach((link) => {
          link.href = target.getAttribute('data-product-url');
        });
      } else if (target.hasAttribute('data-variant-id')) {
        this.querySelectorAll('a[href^="' + Shopify.routes.root + 'products/"]').forEach((link) => {
          var url = new URL(link.href);
          url.searchParams.set('variant', target.getAttribute('data-variant-id'));
          link.href = url.pathname + url.search + url.hash;
        });
      }
      if (!target.hasAttribute('data-variant-media')) return;

      var newMedia = JSON.parse(target.getAttribute('data-variant-media'));
      var primaryMediaElement = this.querySelector('.product-card__image--primary');
      var secondaryMediaElement = this.querySelector('.product-card__image--secondary');
      var newPrimaryMediaElement = this._createMediaImg(newMedia, primaryMediaElement.className, primaryMediaElement.sizes);
      var newSecondaryMediaElement = null;
      if (secondaryMediaElement && target.hasAttribute('data-variant-secondary-media')) {
        var newSecondaryMedia = JSON.parse(target.getAttribute('data-variant-secondary-media'));
        newSecondaryMediaElement = this._createMediaImg(newSecondaryMedia, secondaryMediaElement.className, secondaryMediaElement.sizes);
        newSecondaryMediaElement.ariaHidden = 'true';
      }
      if (primaryMediaElement.src !== newPrimaryMediaElement.src) {
        if (secondaryMediaElement && newSecondaryMediaElement) {
          secondaryMediaElement.replaceWith(newSecondaryMediaElement);
        } else if (secondaryMediaElement && !newSecondaryMediaElement) {
          // The fix: remove the stale secondary element when the new
          // variant has none, rather than leaving the old variant's
          // secondary image in place.
          secondaryMediaElement.remove();
        }
        await primaryMediaElement.animate({ opacity: [1, 0] }, { duration: 150, easing: 'ease-in', fill: 'forwards' }).finished;
        await new Promise((resolve) => newPrimaryMediaElement.complete ? resolve() : (newPrimaryMediaElement.onload = () => resolve()));
        primaryMediaElement.replaceWith(newPrimaryMediaElement);
        newPrimaryMediaElement.animate({ opacity: [0, 1] }, { duration: 150, easing: 'ease-in' });
      }
    }
  }

  if (!window.customElements.get('product-card')) {
    window.customElements.define('product-card', AscotProductCard);
  }
})();
